import React, { useState, useEffect } from 'react';
import { 
    Building2, Ship, Truck, ArrowRight, ArrowLeft, Check, 
    Package, FileText, MapPin, DollarSign, Users, Zap,
    Shield, Clock, BarChart3, Bot, FileCheck, Calculator,
    Loader2, CreditCard, Tag, AlertCircle, CheckCircle2, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Company type options
const COMPANY_TYPES = [
    {
        id: 'BCO',
        name: 'Beneficial Cargo Owner',
        description: 'Shipper or consignee who owns the cargo',
        icon: Package,
        color: 'from-blue-500 to-blue-600'
    },
    {
        id: 'NVOCC',
        name: 'NVOCC',
        description: 'Non-Vessel Operating Common Carrier',
        icon: Ship,
        color: 'from-teal-500 to-teal-600'
    },
    {
        id: 'FreightForwarder',
        name: 'Freight Forwarder',
        description: 'Logistics service provider',
        icon: Truck,
        color: 'from-purple-500 to-purple-600'
    }
];

// Module icons mapping
const MODULE_ICONS = {
    CORE_TMS: Ship,
    RATE_QUOTE: Calculator,
    BOOKING: FileText,
    TRACKING: MapPin,
    DOCUMENTATION: FileCheck,
    ACCOUNTING: DollarSign,
    CONTROL_TOWER: BarChart3,
    AI_AGENTS: Bot,
    CUSTOMS: Shield,
    REPORTING: BarChart3
};

const CompanyWizard = ({ isOpen, onClose, onSuccess }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState({ core_modules: [], addon_modules: [], all_modules: [] });
    const [error, setError] = useState('');
    
    // Form data
    const [formData, setFormData] = useState({
        // Step 1: Company Type
        company_type: '',
        
        // Step 2: Company Details
        company_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: 'USA',
        postal_code: '',
        website: '',
        nvocc_license: '',
        subscription_status: 'active',
        
        // Step 2: Admin User
        admin_email: '',
        admin_password: '',
        admin_confirm_password: '',
        admin_first_name: '',
        admin_last_name: '',
        
        // Step 3: Modules
        selected_modules: ['CORE_TMS'],
    });
    
    // Fetch modules on mount
    useEffect(() => {
        if (isOpen) {
            fetchModules();
            // Reset form when opened
            setCurrentStep(1);
            setError('');
            setFormData({
                company_type: '',
                company_name: '',
                email: '',
                phone: '',
                address: '',
                city: '',
                state: '',
                country: 'USA',
                postal_code: '',
                website: '',
                nvocc_license: '',
                subscription_status: 'active',
                admin_email: '',
                admin_password: '',
                admin_confirm_password: '',
                admin_first_name: '',
                admin_last_name: '',
                selected_modules: ['CORE_TMS'],
            });
        }
    }, [isOpen]);
    
    const fetchModules = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/payments/modules/pricing`);
            if (res.data.success) {
                setModules(res.data);
            }
        } catch (err) {
            console.error('Error fetching modules:', err);
        }
    };
    
    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };
    
    const toggleModule = (moduleCode) => {
        setFormData(prev => {
            const selected = prev.selected_modules.includes(moduleCode)
                ? prev.selected_modules.filter(m => m !== moduleCode)
                : [...prev.selected_modules, moduleCode];
            // Always include CORE_TMS
            if (!selected.includes('CORE_TMS')) {
                selected.push('CORE_TMS');
            }
            return { ...prev, selected_modules: selected };
        });
    };
    
    const calculateTotal = () => {
        let total = 0;
        formData.selected_modules.forEach(code => {
            const module = modules.all_modules.find(m => m.module_code === code);
            if (module) {
                total += parseFloat(module.base_price || 0);
                if (module.pricing_model === 'per_seat') {
                    total += parseFloat(module.per_seat_price || 0);
                }
            }
        });
        return total;
    };
    
    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.company_type) {
                    setError('Please select a company type');
                    return false;
                }
                break;
            case 2:
                if (!formData.company_name || !formData.email || !formData.admin_email || 
                    !formData.admin_password || !formData.admin_first_name || !formData.admin_last_name) {
                    setError('Please fill in all required fields');
                    return false;
                }
                if (formData.admin_password !== formData.admin_confirm_password) {
                    setError('Passwords do not match');
                    return false;
                }
                if (formData.admin_password.length < 8) {
                    setError('Password must be at least 8 characters');
                    return false;
                }
                if (formData.company_type === 'NVOCC' && !formData.nvocc_license) {
                    setError('NVOCC License number is required');
                    return false;
                }
                break;
            case 3:
                if (formData.selected_modules.length === 0) {
                    setError('Please select at least one module');
                    return false;
                }
                break;
        }
        return true;
    };
    
    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 4));
        }
    };
    
    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        setError('');
    };
    
    const handleCreateCompany = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Create company with admin user via signup endpoint
            const signupRes = await axios.post(`${BACKEND_URL}/api/admin/companies/signup`, {
                company_name: formData.company_name,
                company_type: formData.company_type,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                postal_code: formData.postal_code,
                website: formData.website,
                nvocc_license: formData.nvocc_license,
                selected_modules: formData.selected_modules,
                admin_email: formData.admin_email,
                admin_password: formData.admin_password,
                admin_first_name: formData.admin_first_name,
                admin_last_name: formData.admin_last_name
            });
            
            if (!signupRes.data.success) {
                throw new Error(signupRes.data.detail || 'Failed to create company');
            }
            
            // If admin set status to active, update it
            if (formData.subscription_status === 'active') {
                const companyId = signupRes.data.company.id;
                await axios.put(`${BACKEND_URL}/api/admin/companies/${companyId}`, {
                    subscription_status: 'active'
                });
                
                // Activate selected modules
                await axios.post(`${BACKEND_URL}/api/admin/companies/${companyId}/modules`, {
                    company_id: companyId,
                    module_codes: formData.selected_modules,
                    seats: 1
                });
            }
            
            // Success - close wizard and refresh
            onSuccess && onSuccess();
            onClose();
            
        } catch (err) {
            console.error('Error creating company:', err);
            setError(err.response?.data?.detail || err.message || 'An error occurred');
        }
        
        setLoading(false);
    };
    
    // Step indicator
    const steps = [
        { num: 1, label: 'Company Type' },
        { num: 2, label: 'Details' },
        { num: 3, label: 'Modules' },
        { num: 4, label: 'Review' }
    ];
    
    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="company-wizard-modal">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">
                        Add New Company
                    </h2>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>
                
                {/* Progress Steps */}
                <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                    <div className="flex items-center justify-between max-w-2xl mx-auto">
                        {steps.map((step, idx) => (
                            <React.Fragment key={step.num}>
                                <div className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                                        currentStep >= step.num 
                                            ? 'bg-teal-500 text-white' 
                                            : 'bg-slate-700 text-slate-400'
                                    }`}>
                                        {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                                    </div>
                                    <span className={`ml-2 text-xs font-medium hidden sm:block ${
                                        currentStep >= step.num ? 'text-white' : 'text-slate-500'
                                    }`}>
                                        {step.label}
                                    </span>
                                </div>
                                {idx < steps.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-3 ${
                                        currentStep > step.num ? 'bg-teal-500' : 'bg-slate-700'
                                    }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-400 text-sm">{error}</span>
                        </div>
                    )}
                    
                    {/* Step 1: Company Type */}
                    {currentStep === 1 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">Select Company Type</h3>
                            <p className="text-slate-400 text-sm mb-6">Choose the type that best describes the business</p>
                            
                            <div className="grid md:grid-cols-3 gap-4">
                                {COMPANY_TYPES.map(type => (
                                    <div
                                        key={type.id}
                                        onClick={() => updateFormData('company_type', type.id)}
                                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                                            formData.company_type === type.id
                                                ? 'border-teal-500 bg-teal-500/10'
                                                : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                                        }`}
                                    >
                                        {formData.company_type === type.id && (
                                            <div className="absolute top-3 right-3">
                                                <CheckCircle2 className="w-5 h-5 text-teal-500" />
                                            </div>
                                        )}
                                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-3`}>
                                            <type.icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h4 className="text-base font-semibold text-white mb-1">{type.name}</h4>
                                        <p className="text-xs text-slate-400">{type.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Step 2: Company Details */}
                    {currentStep === 2 && (
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Company Details */}
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-teal-500" /> Company Info
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Company Name *</label>
                                        <input
                                            type="text"
                                            value={formData.company_name}
                                            onChange={(e) => updateFormData('company_name', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Acme Logistics Inc."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Email *</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => updateFormData('email', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="info@company.com"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Phone</label>
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => updateFormData('phone', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="+1-555-123-4567"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Website</label>
                                            <input
                                                type="url"
                                                value={formData.website}
                                                onChange={(e) => updateFormData('website', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="www.company.com"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Address</label>
                                        <input
                                            type="text"
                                            value={formData.address}
                                            onChange={(e) => updateFormData('address', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="123 Business Ave"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">City</label>
                                            <input
                                                type="text"
                                                value={formData.city}
                                                onChange={(e) => updateFormData('city', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="Los Angeles"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">State</label>
                                            <input
                                                type="text"
                                                value={formData.state}
                                                onChange={(e) => updateFormData('state', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="California"
                                            />
                                        </div>
                                    </div>
                                    {formData.company_type === 'NVOCC' && (
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">NVOCC License *</label>
                                            <input
                                                type="text"
                                                value={formData.nvocc_license}
                                                onChange={(e) => updateFormData('nvocc_license', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="FMC License #"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Initial Status</label>
                                        <select
                                            value={formData.subscription_status}
                                            onChange={(e) => updateFormData('subscription_status', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        >
                                            <option value="active">Active</option>
                                            <option value="trial">Trial</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Admin User */}
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-teal-500" /> Admin Account
                                </h3>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">First Name *</label>
                                            <input
                                                type="text"
                                                value={formData.admin_first_name}
                                                onChange={(e) => updateFormData('admin_first_name', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="John"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Last Name *</label>
                                            <input
                                                type="text"
                                                value={formData.admin_last_name}
                                                onChange={(e) => updateFormData('admin_last_name', e.target.value)}
                                                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Admin Email *</label>
                                        <input
                                            type="email"
                                            value={formData.admin_email}
                                            onChange={(e) => updateFormData('admin_email', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="admin@company.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Password *</label>
                                        <input
                                            type="password"
                                            value={formData.admin_password}
                                            onChange={(e) => updateFormData('admin_password', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Min 8 characters"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Confirm Password *</label>
                                        <input
                                            type="password"
                                            value={formData.admin_confirm_password}
                                            onChange={(e) => updateFormData('admin_confirm_password', e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            placeholder="Confirm password"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Step 3: Module Selection */}
                    {currentStep === 3 && (
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">Select Modules</h3>
                            <p className="text-slate-400 text-sm mb-4">Core TMS is required. Select additional modules.</p>
                            
                            {/* Core Modules */}
                            <div className="mb-6">
                                <h4 className="text-xs font-semibold text-teal-400 uppercase tracking-wider mb-3">Core Modules</h4>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {modules.all_modules.filter(m => ['CORE_TMS', 'BOOKING', 'RATE_QUOTE', 'TRACKING'].includes(m.module_code)).map(module => {
                                        const Icon = MODULE_ICONS[module.module_code] || Package;
                                        const isSelected = formData.selected_modules.includes(module.module_code);
                                        const isRequired = module.module_code === 'CORE_TMS';
                                        
                                        return (
                                            <div
                                                key={module.module_code}
                                                onClick={() => !isRequired && toggleModule(module.module_code)}
                                                className={`relative p-3 rounded-lg border-2 transition-all ${
                                                    isRequired ? 'cursor-default' : 'cursor-pointer'
                                                } ${
                                                    isSelected
                                                        ? 'border-teal-500 bg-teal-500/10'
                                                        : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                                                }`}
                                            >
                                                {isRequired && (
                                                    <span className="absolute top-2 right-2 text-[9px] bg-teal-500 text-white px-1.5 py-0.5 rounded">Required</span>
                                                )}
                                                {isSelected && !isRequired && (
                                                    <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-teal-500" />
                                                )}
                                                <div className="flex items-start gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                                                        <Icon className="w-4 h-4 text-teal-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="font-medium text-white text-sm truncate">{module.module_name}</h5>
                                                        <div className="flex items-baseline gap-1 mt-1">
                                                            <span className="text-sm font-bold text-white">${module.base_price}</span>
                                                            <span className="text-xs text-slate-400">/mo</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* Add-on Modules */}
                            <div>
                                <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-3">Add-on Modules</h4>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {modules.all_modules.filter(m => !['CORE_TMS', 'BOOKING', 'RATE_QUOTE', 'TRACKING'].includes(m.module_code)).map(module => {
                                        const Icon = MODULE_ICONS[module.module_code] || Package;
                                        const isSelected = formData.selected_modules.includes(module.module_code);
                                        
                                        return (
                                            <div
                                                key={module.module_code}
                                                onClick={() => toggleModule(module.module_code)}
                                                className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                    isSelected
                                                        ? 'border-purple-500 bg-purple-500/10'
                                                        : 'border-slate-600 hover:border-slate-500 bg-slate-800/50'
                                                }`}
                                            >
                                                {isSelected && (
                                                    <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-purple-500" />
                                                )}
                                                <div className="flex items-start gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                                                        <Icon className="w-4 h-4 text-purple-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="font-medium text-white text-sm truncate">{module.module_name}</h5>
                                                        <div className="flex items-baseline gap-1 mt-1">
                                                            <span className="text-sm font-bold text-white">${module.base_price}</span>
                                                            <span className="text-xs text-slate-400">/mo</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* Running Total */}
                            <div className="mt-4 p-3 bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-between">
                                <div className="text-sm">
                                    <span className="text-slate-400">Selected:</span>
                                    <span className="ml-2 text-white font-medium">{formData.selected_modules.length} modules</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-slate-400 text-sm">Monthly:</span>
                                    <span className="ml-2 text-xl font-bold text-teal-400">${calculateTotal().toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Step 4: Review */}
                    {currentStep === 4 && (
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Summary */}
                            <div className="space-y-4">
                                {/* Company Summary */}
                                <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                                    <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Company</h4>
                                    <p className="text-white font-medium">{formData.company_name}</p>
                                    <p className="text-sm text-slate-400">{formData.company_type} • {formData.email}</p>
                                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded ${
                                        formData.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                                        formData.subscription_status === 'trial' ? 'bg-blue-500/20 text-blue-400' :
                                        'bg-amber-500/20 text-amber-400'
                                    }`}>
                                        {formData.subscription_status}
                                    </span>
                                </div>
                                
                                {/* Admin Summary */}
                                <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                                    <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Admin Account</h4>
                                    <p className="text-white font-medium">{formData.admin_first_name} {formData.admin_last_name}</p>
                                    <p className="text-sm text-slate-400">{formData.admin_email}</p>
                                </div>
                                
                                {/* Modules */}
                                <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                                    <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">
                                        Modules ({formData.selected_modules.length})
                                    </h4>
                                    <div className="space-y-1">
                                        {formData.selected_modules.map(code => {
                                            const module = modules.all_modules.find(m => m.module_code === code);
                                            if (!module) return null;
                                            return (
                                                <div key={code} className="flex items-center justify-between text-sm">
                                                    <span className="text-white">{module.module_name}</span>
                                                    <span className="text-slate-400">${module.base_price}/mo</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Billing Summary */}
                            <div className="p-5 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700">
                                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-teal-500" /> Billing Summary
                                </h4>
                                
                                <div className="space-y-2 mb-4 pb-4 border-b border-slate-700">
                                    {formData.selected_modules.map(code => {
                                        const module = modules.all_modules.find(m => m.module_code === code);
                                        if (!module) return null;
                                        let amount = parseFloat(module.base_price);
                                        if (module.pricing_model === 'per_seat') {
                                            amount += parseFloat(module.per_seat_price);
                                        }
                                        return (
                                            <div key={code} className="flex justify-between text-sm">
                                                <span className="text-slate-300">{module.module_name}</span>
                                                <span className="text-white">${amount.toFixed(2)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                <div className="flex justify-between items-baseline pt-2">
                                    <span className="text-slate-300">Monthly Total</span>
                                    <span className="text-2xl font-bold text-teal-400">${calculateTotal().toFixed(2)}</span>
                                </div>
                                
                                <p className="text-xs text-slate-500 mt-2">
                                    {formData.subscription_status === 'active' 
                                        ? 'Company will be activated immediately'
                                        : formData.subscription_status === 'trial'
                                        ? 'Company will start with a trial period'
                                        : 'Company will be pending activation'
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="bg-slate-800 border-t border-slate-700 px-6 py-4 flex justify-between">
                    <button
                        onClick={currentStep === 1 ? onClose : prevStep}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> {currentStep === 1 ? 'Cancel' : 'Back'}
                    </button>
                    
                    {currentStep < 4 ? (
                        <button
                            onClick={nextStep}
                            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleCreateCompany}
                            disabled={loading}
                            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Create Company
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyWizard;
